import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState } from '@teivor/shared-ui';

interface State {
  error: Error | null;
}

interface InnerProps {
  children: React.ReactNode;
  routeKey: string;
}

class PageErrorBoundaryInner extends React.Component<InnerProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prevProps: InnerProps) {
    if (prevProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[PageErrorBoundary]', error, info.componentStack);
    (window as unknown as { Sentry?: { captureException: (e: Error, ctx?: unknown) => void } }).Sentry?.captureException(
      error,
      { contexts: { react: { componentStack: info.componentStack } } },
    );
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} onReset={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

function ErrorFallback({ error, onReset }: { error: Error; onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
      {/* Shared, consistent error UX (icon + title + description + retry). */}
      <ErrorState
        title={t('errors.page_crashed_title', 'This page could not be displayed.')}
        description={t(
          'errors.page_crashed_description',
          'An unexpected error occurred while rendering. You can reload the page or go back home.',
        )}
        retryLabel={t('errors.try_again', 'Try again')}
        onRetry={onReset}
      />
      <div className="mt-2 flex w-full max-w-xl flex-col items-center gap-3">
        <details className="w-full rounded-[var(--radius-md)] bg-[var(--color-subtle)] p-3 text-xs text-[var(--color-fg-secondary)]">
          <summary className="cursor-pointer font-mono text-[11px]">
            {t('errors.page_crashed_details', 'Technical details')}
          </summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words font-mono text-[11px]">
            {error.message}
          </pre>
        </details>
        <a
          href="/"
          className="inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium hover:bg-[var(--color-subtle)]"
        >
          {t('errors.go_home', 'Go home')}
        </a>
      </div>
    </div>
  );
}

export function PageErrorBoundary({ children, routeKey }: InnerProps) {
  return <PageErrorBoundaryInner routeKey={routeKey}>{children}</PageErrorBoundaryInner>;
}
